import React from 'react';
import { Download, FileDown, Share2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { exportTableCsv, exportTablePdf, shareTableContent } from '@/lib/riqueExport';

export interface RiqueDataTableProps {
  title?: string;
  columns: string[];
  rows: string[][];
  /** Limite de linhas visíveis; omitir = todas */
  rowLimit?: number;
  showActions?: boolean;
  className?: string;
}

/** Heurística: coluna numérica se a maioria das células parece número / R$ / %. */
export function isNumericColumn(rows: string[][], colIdx: number): boolean {
  let hits = 0;
  let samples = 0;
  for (const row of rows) {
    const cell = (row[colIdx] ?? '').trim();
    if (!cell || cell === '—' || cell === '-') continue;
    samples += 1;
    if (
      /^(R\$\s*)?-?[\d.,]+%?$/.test(cell) ||
      /^-?[\d]+([.,]\d+)?%?$/.test(cell.replace(/\s/g, ''))
    ) {
      hits += 1;
    }
  }
  return samples > 0 && hits / samples >= 0.6;
}

function splitPipeRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

function isSeparatorRow(line: string): boolean {
  const cells = splitPipeRow(line);
  if (cells.length === 0) return false;
  return cells.every((c) => /^:?-{3,}:?$/.test(c.replace(/\s/g, '')));
}

export function isPipeTableHeaderLine(line: string): boolean {
  const t = line.trim();
  return t.includes('|') && !isSeparatorRow(t) && splitPipeRow(t).length >= 2;
}

/** Consome linhas a partir de `start` se formarem uma tabela GFM. */
export function tryParseGfmTable(
  lines: string[],
  start: number
): { columns: string[]; rows: string[][]; end: number } | null {
  if (start + 1 >= lines.length) return null;
  const headerLine = lines[start]!.trim();
  const sepLine = lines[start + 1]!.trim();
  if (!isPipeTableHeaderLine(headerLine) || !isSeparatorRow(sepLine)) return null;

  const columns = splitPipeRow(headerLine);
  if (columns.length < 2) return null;

  const rows: string[][] = [];
  let i = start + 2;
  while (i < lines.length) {
    const raw = lines[i]!.trim();
    if (!raw) break;
    if (!raw.includes('|') || isSeparatorRow(raw)) break;
    const cells = splitPipeRow(raw);
    if (cells.length === 0) break;
    const row = columns.map((_, idx) => cells[idx] ?? '');
    rows.push(row);
    i += 1;
  }

  return { columns, rows, end: i };
}

function ActionButton({
  label,
  title,
  onClick,
  icon,
}: {
  label: string;
  title: string;
  onClick: () => void;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-border/80 bg-background px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
      title={title}
    >
      {icon}
      {label}
    </button>
  );
}

export const RiqueDataTable: React.FC<RiqueDataTableProps> = ({
  title,
  columns,
  rows,
  rowLimit,
  showActions = true,
  className,
}) => {
  const [sharing, setSharing] = React.useState(false);
  const numericFlags = React.useMemo(
    () => columns.map((_, idx) => isNumericColumn(rows, idx)),
    [columns, rows]
  );

  const limit = typeof rowLimit === 'number' && rowLimit > 0 ? rowLimit : rows.length;
  const visible = rows.slice(0, limit);
  const hidden = Math.max(0, rows.length - visible.length);
  const safeTitle = title?.trim() || 'Tabela';

  const handlePdf = () => {
    try {
      exportTablePdf(safeTitle, columns, rows);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao exportar PDF');
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const channel = await shareTableContent(safeTitle, columns, rows);
      if (channel === 'clipboard') toast.success('Tabela copiada. Cole onde quiser compartilhar.');
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      toast.error(e instanceof Error ? e.message : 'Não foi possível compartilhar');
    } finally {
      setSharing(false);
    }
  };

  return (
    <div
      className={cn(
        'my-2 overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm',
        className
      )}
    >
      {(title || showActions) && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 bg-muted/30 px-3 py-2">
          {title ? (
            <p className="min-w-0 flex-1 truncate text-xs font-semibold text-foreground">{title}</p>
          ) : (
            <span className="flex-1" />
          )}
          {showActions && columns.length > 0 ? (
            <div className="flex flex-wrap items-center justify-end gap-1">
              <ActionButton
                label="CSV"
                title="Exportar CSV"
                icon={<Download className="h-3 w-3" aria-hidden />}
                onClick={() => exportTableCsv(safeTitle, columns, rows)}
              />
              <ActionButton
                label="PDF"
                title="Exportar PDF"
                icon={<FileDown className="h-3 w-3" aria-hidden />}
                onClick={handlePdf}
              />
              <ActionButton
                label={sharing ? '…' : 'Compartilhar'}
                title="Compartilhar tabela"
                icon={<Share2 className="h-3 w-3" aria-hidden />}
                onClick={() => void handleShare()}
              />
            </div>
          ) : null}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[16rem] border-collapse text-sm">
          <thead>
            <tr className="bg-muted/40">
              {columns.map((col, idx) => (
                <th
                  key={`${col}-${idx}`}
                  className={cn(
                    'sticky top-0 whitespace-nowrap px-3 py-2 text-left text-xs font-semibold text-muted-foreground',
                    numericFlags[idx] && 'text-right'
                  )}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={cn(
                  'border-t border-border/50',
                  rowIdx % 2 === 1 && 'bg-muted/20'
                )}
              >
                {columns.map((_, cellIdx) => (
                  <td
                    key={cellIdx}
                    className={cn(
                      'px-3 py-2 text-foreground',
                      numericFlags[cellIdx] && 'text-right tabular-nums'
                    )}
                  >
                    {row[cellIdx] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hidden > 0 ? (
        <p className="border-t border-border/50 px-3 py-1.5 text-[11px] text-muted-foreground">
          +{hidden} linhas — exporte CSV ou PDF para ver tudo
        </p>
      ) : null}
    </div>
  );
};
